import React, { useState, useEffect, useRef } from 'react';
import { FiAlertCircle, FiX, FiSend, FiLoader, FiCamera, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../utils/api';

export default function BugReportButton() {
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useAuth();

  return (
    <>
      {/* Floating bug report button */}
      <button
        onClick={() => setIsOpen(true)}
        className="bug-report-fab"
        aria-label="Report an issue"
        title="Report an issue"
      >
        <FiAlertCircle size={20} />
        <span className="bug-report-fab-tooltip">Report an issue</span>
      </button>

      {isOpen && (
        <BugReportModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          user={user}
        />
      )}
    </>
  );
}

function BugReportModal({ isOpen, onClose, user }) {
  const [description, setDescription] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [category, setCategory] = useState('bug');
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setDescription('');
      setGuestEmail('');
      setCategory('bug');
      setScreenshot(null);
      setScreenshotPreview(null);
      setSubmitted(false);
      setError(null);
    }
  }, [isOpen]);

  const handleScreenshotChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('Screenshot must be under 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    setScreenshot(file);
    setError(null);

    const reader = new FileReader();
    reader.onload = (ev) => setScreenshotPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    setScreenshotPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!description.trim()) {
      setError('Please describe the issue');
      return;
    }

    if (!user && !guestEmail.trim()) {
      setError('Please add an email so we can follow up');
      return;
    }

    if (!user && guestEmail.trim()) {
      const email = guestEmail.trim();
      const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      if (!isValid) {
        setError('Please enter a valid email address');
        return;
      }
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Convert screenshot to base64 if present
      let screenshotBase64 = null;
      let screenshotFilename = null;
      let screenshotMimeType = null;
      if (screenshot) {
        screenshotFilename = screenshot.name;
        screenshotMimeType = screenshot.type;
        screenshotBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (ev) => {
            // Strip the data:image/...;base64, prefix
            const base64 = ev.target.result.split(',')[1];
            resolve(base64);
          };
          reader.readAsDataURL(screenshot);
        });
      }

      // Gather context data
      const contextData = {
        // User info
        userId: user?.id || null,
        userEmail: user?.email || guestEmail.trim() || null,
        userName: user?.name || user?.display_name || null,
        guestEmail: !user ? guestEmail.trim() || null : null,

        // Timing
        timestamp: new Date().toISOString(),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,

        // Browser/Device info
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        devicePixelRatio: window.devicePixelRatio,

        // Page info
        currentUrl: window.location.href,
        pathname: window.location.pathname,
        referrer: document.referrer || null,

        // Report content
        category,
        description: description.trim(),

        // Screenshot
        screenshot: screenshotBase64,
        screenshotFilename,
        screenshotMimeType,
      };

      const res = await apiFetch('/api/bug-report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contextData),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to submit report');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit report');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="bug-report-overlay" onClick={onClose}>
      <div className="bug-report-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bug-report-header">
          <div className="bug-report-title">
            <FiAlertCircle size={20} className="bug-report-icon" />
            <h2>Report an Issue</h2>
          </div>
          <button onClick={onClose} className="bug-report-close">
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="bug-report-content">
          {submitted ? (
            <div className="bug-report-success">
              <div className="bug-report-success-icon">
                <svg width="32" height="32" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3>Thank you!</h3>
              <p>Your report has been submitted. We'll look into it as soon as possible.</p>
              <button onClick={onClose} className="bug-report-btn primary">
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bug-report-form">
              {/* Category */}
              <div className="bug-report-field">
                <label>Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="bug">Bug / Something broken</option>
                  <option value="feature">Feature request</option>
                  <option value="ui">UI/UX issue</option>
                  <option value="performance">Performance issue</option>
                  <option value="upload">Upload problem</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Description */}
              <div className="bug-report-field">
                <label>Describe the issue</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Please describe what happened, what you expected, and any steps to reproduce..."
                  rows={5}
                />
              </div>

              {/* Screenshot */}
              <div className="bug-report-field">
                <label>Screenshot (optional)</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleScreenshotChange}
                  style={{ display: 'none' }}
                />
                {screenshotPreview ? (
                  <div className="bug-report-screenshot-preview">
                    <img src={screenshotPreview} alt="Screenshot preview" />
                    <button
                      type="button"
                      onClick={removeScreenshot}
                      className="bug-report-screenshot-remove"
                      title="Remove screenshot"
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="bug-report-screenshot-btn"
                  >
                    <FiCamera size={16} />
                    Attach a screenshot
                  </button>
                )}
              </div>

              {/* Guest email (if not logged in) */}
              {!user && (
                <div className="bug-report-field">
                  <label>Your email (for follow-up)</label>
                  <input
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="you@example.com"
                  />
                </div>
              )}

              {/* Context info preview */}
              <div className="bug-report-context">
                <p className="bug-report-context-title">We'll also include:</p>
                <ul>
                  <li>Current page: {window.location.pathname}</li>
                  <li>Browser & device info</li>
                  <li>Timestamp</li>
                  {user ? <li>Your account info (for follow-up)</li> : <li>Your email (for follow-up)</li>}
                </ul>
              </div>

              {/* Error message */}
              {error && (
                <div className="bug-report-error">
                  {error}
                </div>
              )}

              {/* Submit button */}
              <button
                type="submit"
                className="bug-report-btn primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <FiLoader className="spinning" size={16} />
                    Submitting...
                  </>
                ) : (
                  <>
                    <FiSend size={16} />
                    Submit Report
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
